(function () {
  const root = document.querySelector("[data-xp-shell]");
  if (!root) return;

  /* ---------- Boot screen (shows once per browser session) ---------- */
  const bootScreen = root.querySelector("[data-xp-boot]");
  if (bootScreen) {
    let booted = false;
    try {
      booted = !!sessionStorage.getItem("xpBooted");
    } catch (e) { /* storage unavailable — always boot */ }

    if (booted) {
      bootScreen.remove();
    } else {
      try { sessionStorage.setItem("xpBooted", "1"); } catch (e) {}
      setTimeout(() => {
        bootScreen.classList.add("is-done");
        setTimeout(() => bootScreen.remove(), 650);
      }, 2800);
    }
  }

  const windows = Array.from(root.querySelectorAll("[data-window]"));
  const openers = Array.from(root.querySelectorAll("[data-open-window]"));
  const startToggle = root.querySelector("[data-start-toggle]");
  const startMenu = root.querySelector("[data-start-menu]");
  const taskbarPrograms = root.querySelector("[data-taskbar-programs]");
  const clock = root.querySelector("[data-xp-clock]");
  const desktopIconsRoot = root.querySelector(".xpDesktop__icons");

  let zIndex = 20;
  let activeWindow = null;

  // Track which windows have been manually positioned (dragged by user)
  const userPositionedWindows = new Set();

  /* ---------- Clock ---------- */
  function updateClock() {
    if (!clock) return;
    const now = new Date();
    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const suffix = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    clock.textContent = `${hours}:${minutes} ${suffix}`;
  }

  /* ---------- Window helpers ---------- */
  function getWindow(name) {
    return root.querySelector(`[data-window="${name}"]`);
  }

  function getTaskbarButton(name) {
    if (!taskbarPrograms) return null;
    return taskbarPrograms.querySelector(`[data-taskbar-window="${name}"]`);
  }

  function getOpenerButton(name) {
    return root.querySelector(`[data-open-window="${name}"]`);
  }

  function setActiveWindow(win) {
    if (activeWindow === win) return;
    windows.forEach((w) => w.classList.add("is-inactive"));
    if (win) {
      win.classList.remove("is-inactive");
    }
    if (taskbarPrograms) {
      taskbarPrograms
        .querySelectorAll(".xpTaskbar__program")
        .forEach((b) => b.classList.remove("is-active"));
      if (win) {
        const name = win.getAttribute("data-window");
        const btn = getTaskbarButton(name);
        if (btn) btn.classList.add("is-active");
      }
    }
    activeWindow = win;
  }

  function bringToFront(win) {
    zIndex += 1;
    win.style.zIndex = zIndex;
    setActiveWindow(win);
  }

  /* ---------- Smart positioning: avoid overlap ---------- */
  function findNonOverlappingPosition(win) {
    const name = win.getAttribute("data-window");
    if (!name) return null;
    
    // Skip centered alert-style windows — they stay put
    const centeredWindows = ["form-alert", "unlock", "age-alert"];
    if (centeredWindows.includes(name)) return null;
    
    // If user already dragged this window, respect that position
    if (userPositionedWindows.has(name)) return null;

    // Get all currently visible windows (not hidden, not minimized, not this one)
    const visibleWindows = windows.filter(
      (w) =>
        w !== win &&
        !centeredWindows.includes(w.getAttribute("data-window")) &&
        !w.classList.contains("is-hidden") &&
        !w.classList.contains("is-minimized")
    );

    // Estimate dimensions (use defaults if not rendered yet)
    const winW = win.offsetWidth || 720;
    const winH = win.offsetHeight || 450;
    
    const taskbarHeight = 30;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight - taskbarHeight;

    // Build list of occupied rectangles
    const occupiedRects = visibleWindows.map((w) => {
      const rect = w.getBoundingClientRect();
      return {
        left: rect.left,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height
      };
    });

    // Helper: check if a candidate position overlaps with any occupied rect
    function overlaps(left, top) {
      const right = left + winW;
      const bottom = top + winH;

      // Check viewport bounds
      if (left < 0 || top < 0 || right > viewportW || bottom > viewportH) {
        return true;
      }

      // Check overlap with existing windows
      for (const rect of occupiedRects) {
        const noOverlap =
          right <= rect.left ||
          left >= rect.right ||
          bottom <= rect.top ||
          top >= rect.bottom;
        if (!noOverlap) return true;
      }
      return false;
    }

    const margin = 20;
    const stepX = 40;
    const stepY = 40;

    // Try top-left
    if (!overlaps(margin, margin)) {
      return { left: margin, top: margin };
    }

    // Try along top edge
    for (let x = margin; x + winW <= viewportW - margin; x += stepX) {
      if (!overlaps(x, margin)) {
        return { left: x, top: margin };
      }
    }

    // Try along left edge
    for (let y = margin; y + winH <= viewportH - margin; y += stepY) {
      if (!overlaps(margin, y)) {
        return { left: margin, top: y };
      }
    }

    // Try grid across the viewport
    for (let y = margin; y + winH <= viewportH - margin; y += stepY) {
      for (let x = margin; x + winW <= viewportW - margin; x += stepX) {
        if (!overlaps(x, y)) {
          return { left: x, top: y };
        }
      }
    }

    // Fallback: cascade over the last visible window
    if (visibleWindows.length > 0) {
      const lastWin = visibleWindows[visibleWindows.length - 1];
      const lastRect = lastWin.getBoundingClientRect();
      const cascadeLeft = Math.min(lastRect.left + 32, viewportW - winW - margin);
      const cascadeTop = Math.min(lastRect.top + 30, viewportH - winH - margin);
      return { left: Math.max(margin, cascadeLeft), top: Math.max(margin, cascadeTop) };
    }

    // Absolute fallback: center of screen
    return {
      left: Math.max(margin, (viewportW - winW) / 2),
      top: Math.max(margin, (viewportH - winH) / 2)
    };
  }

  function applySmartPosition(win) {
    const pos = findNonOverlappingPosition(win);
    if (!pos) return; // null means skip (centered alert or user-positioned)

    win.style.left = `${pos.left}px`;
    win.style.top = `${pos.top}px`;
    win.style.transform = ""; // clear any centering transform
  }

  /* ---------- Set transform-origin from launching icon ---------- */
  function setOpenOrigin(win, launcherEl) {
    if (!launcherEl) {
      win.style.removeProperty("--xp-origin-x");
      win.style.removeProperty("--xp-origin-y");
      return;
    }
    const winRect = win.getBoundingClientRect();
    const launchRect = launcherEl.getBoundingClientRect();

    const cx = launchRect.left + launchRect.width / 2;
    const cy = launchRect.top + launchRect.height / 2;

    const winLeft = winRect.left || win.offsetLeft;
    const winTop = winRect.top || win.offsetTop;
    const winW = winRect.width || win.offsetWidth || 720;
    const winH = winRect.height || win.offsetHeight || 400;

    const ox = ((cx - winLeft) / winW) * 100;
    const oy = ((cy - winTop) / winH) * 100;

    win.style.setProperty("--xp-origin-x", `${ox}%`);
    win.style.setProperty("--xp-origin-y", `${oy}%`);
  }

  /* ---------- Calculate translate distance to taskbar button (for minimize) ---------- */
  function setMinimizeTarget(win) {
    const name = win.getAttribute("data-window");
    const btn = getTaskbarButton(name);
    const winRect = win.getBoundingClientRect();
    const winCenterX = winRect.left + winRect.width / 2;
    const winCenterY = winRect.top + winRect.height / 2;

    let targetX, targetY;
    if (btn) {
      const btnRect = btn.getBoundingClientRect();
      targetX = btnRect.left + btnRect.width / 2;
      targetY = btnRect.top + btnRect.height / 2;
    } else {
      targetX = 60;
      targetY = window.innerHeight;
    }

    const dx = targetX - winCenterX;
    const dy = targetY - winCenterY;

    win.style.setProperty("--xp-min-dx", `${dx}px`);
    win.style.setProperty("--xp-min-dy", `${dy}px`);

    win.style.setProperty("--xp-origin-x", `50%`);
    win.style.setProperty("--xp-origin-y", `50%`);
  }

  /* ---------- Taskbar button management ---------- */
  function createTaskbarButton(name, win) {
    if (!taskbarPrograms) return null;

    let btn = getTaskbarButton(name);
    if (btn) return btn;

    btn = document.createElement("button");
    btn.className = "xpTaskbar__program";
    btn.setAttribute("data-taskbar-window", name);
    btn.type = "button";

    const iconHtml =
      (win && win.querySelector(".xpWindow__titleIcon")
        ? win.querySelector(".xpWindow__titleIcon").innerHTML
        : "");

    const labels = {
      workwithus: "Work With Us",
      "form-alert": "Message Sent",
      "age-alert": "Application Blocked",
      legal: "Legal",
      unlock: "Staff Login",
      submission: "Submission",
      recruitment: "Recruitment",
      services: "Services",
      merch: "Merch",
      about: "About Us",
      contact: "Contact",
      "drop-100k": "100K Drop",
      "drop-2024": "2024 Drop",
      "drop-2026": "2026 Drop"
    };

    const label = labels[name] || name;

    btn.innerHTML = `
      <span class="xpTaskbar__program__icon">${iconHtml}</span>
      <span class="xpTaskbar__program__label">${label}</span>
    `;

    btn.addEventListener("click", () => {
      const w = getWindow(name);
      if (!w) return;

      const isMinimized = w.classList.contains("is-minimized");
      const isActive = activeWindow === w;

      if (isMinimized) {
        restoreFromMinimize(name);
      } else if (isActive) {
        minimizeWindow(name);
      } else {
        bringToFront(w);
      }
    });

    taskbarPrograms.appendChild(btn);
    return btn;
  }

  function removeTaskbarButton(name) {
    const btn = getTaskbarButton(name);
    if (btn) btn.remove();
  }

  /* ---------- Open / Close / Minimize / Restore ---------- */
  function openWindow(name, launcherEl) {
    const win = getWindow(name);
    if (!win) return;

    // already open and visible? just focus
    if (
      !win.classList.contains("is-hidden") &&
      !win.classList.contains("is-minimized")
    ) {
      bringToFront(win);
      return;
    }

    // restore from minimize
    if (win.classList.contains("is-minimized")) {
      restoreFromMinimize(name);
      return;
    }

    // first show — apply smart positioning
    win.classList.remove("is-hidden");
    win.style.visibility = "hidden";
    applySmartPosition(win);
    win.style.visibility = "";
    win.classList.add("is-hidden");

    setOpenOrigin(win, launcherEl);

    win.classList.add("is-opening");
    win.classList.remove("is-hidden");

    void win.offsetWidth;

    setOpenOrigin(win, launcherEl);

    requestAnimationFrame(() => {
      win.classList.remove("is-opening");
      bringToFront(win);
    });

    createTaskbarButton(name, win);

    if (startMenu) {
      closeStartMenu();
    }
  }

  function closeWindow(name) {
    const win = getWindow(name);
    if (!win) return;

    if (win.classList.contains("is-hidden")) return;

    win.classList.add("is-closing");

    const onEnd = (e) => {
      if (e.propertyName !== "transform" && e.propertyName !== "opacity") return;
      win.removeEventListener("transitionend", onEnd);
      win.classList.remove("is-closing");
      win.classList.add("is-hidden");
      removeTaskbarButton(name);
      
      if (activeWindow === win) {
        activeWindow = null;
        const next = windows.find(
          (w) =>
            w !== win &&
            !w.classList.contains("is-hidden") &&
            !w.classList.contains("is-minimized")
        );
        if (next) bringToFront(next);
      }
    };
    win.addEventListener("transitionend", onEnd);

    setTimeout(() => {
      if (win.classList.contains("is-closing")) {
        win.classList.remove("is-closing");
        win.classList.add("is-hidden");
        removeTaskbarButton(name);
      }
    }, 400);
  }

  function minimizeWindow(name) {
    const win = getWindow(name);
    if (!win) return;
    if (win.classList.contains("is-hidden")) return;
    if (win.classList.contains("is-minimized")) return;

    setMinimizeTarget(win);

    win.classList.add("is-minimizing");

    const onEnd = (e) => {
      if (e.propertyName !== "transform" && e.propertyName !== "opacity") return;
      win.removeEventListener("transitionend", onEnd);
      win.classList.remove("is-minimizing");
      win.classList.add("is-minimized");
      if (activeWindow === win) {
        activeWindow = null;
        if (taskbarPrograms) {
          const btn = getTaskbarButton(name);
          if (btn) btn.classList.remove("is-active");
        }
      }
    };
    win.addEventListener("transitionend", onEnd);

    setTimeout(() => {
      if (win.classList.contains("is-minimizing")) {
        win.classList.remove("is-minimizing");
        win.classList.add("is-minimized");
      }
    }, 350);
  }

  function restoreFromMinimize(name) {
    const win = getWindow(name);
    if (!win) return;
    if (!win.classList.contains("is-minimized")) return;

    setMinimizeTarget(win);

    win.classList.remove("is-minimized");
    win.classList.add("is-minimizing");
    win.style.visibility = "visible";

    void win.offsetWidth;

    requestAnimationFrame(() => {
      win.classList.remove("is-minimizing");
      bringToFront(win);
    });
  }

  function toggleMaximize(name) {
    const win = getWindow(name);
    if (!win) return;
    win.classList.toggle("is-maximized");
    bringToFront(win);
  }

  /* ---------- Start menu ---------- */
  function openStartMenu() {
    if (!startMenu) return;
    startMenu.classList.add("is-open");
    if (startToggle) startToggle.classList.add("is-active");
  }

  function closeStartMenu() {
    if (!startMenu) return;
    startMenu.classList.remove("is-open");
    if (startToggle) startToggle.classList.remove("is-active");
  }

  function toggleStartMenu() {
    if (!startMenu) return;
    if (startMenu.classList.contains("is-open")) closeStartMenu();
    else openStartMenu();
  }

  if (startToggle && startMenu) {
    startToggle.addEventListener("click", (e) => {
      e.stopPropagation();
      toggleStartMenu();
    });

    document.addEventListener("click", (e) => {
      if (startMenu.classList.contains("is-open")) {
        if (
          !startMenu.contains(e.target) &&
          !startToggle.contains(e.target)
        ) {
          closeStartMenu();
        }
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeStartMenu();
    });
  }

  /* ---------- Wire up icon openers ---------- */
  openers.forEach((btn) => {
    btn.addEventListener("click", (e) => {
      const name = btn.getAttribute("data-open-window");
      openWindow(name, btn);
    });
  });

  /* ---------- Desktop icon selection ---------- */
  if (desktopIconsRoot) {
    const allDesktopIcons = Array.from(
      desktopIconsRoot.querySelectorAll(".xpIcon")
    );
    allDesktopIcons.forEach((icon) => {
      icon.addEventListener("click", (e) => {
        e.stopPropagation();
        allDesktopIcons.forEach((i) => i.classList.remove("is-selected"));
        icon.classList.add("is-selected");
      });
    });

    root.addEventListener("click", (e) => {
      if (
        e.target === root ||
        e.target.classList.contains("xpDesktop") ||
        e.target.classList.contains("xpDesktop__icons") ||
        e.target.classList.contains("xpShell__wallpaper")
      ) {
        allDesktopIcons.forEach((i) => i.classList.remove("is-selected"));
      }
    });
  }

  /* ---------- Right-click gag: block the context menu, shake instead ----------
     Cosmetic nostalgia bit, not a security measure — Ctrl+Shift+I / F12 / Ctrl+U
     still open dev tools, and there's no way to prevent that in a browser. */
  const desktopEl = root.querySelector(".xpDesktop");
  root.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    if (!desktopEl) return;
    desktopEl.classList.remove("is-rightClickShake");
    void desktopEl.offsetWidth; // restart the animation
    desktopEl.classList.add("is-rightClickShake");
  });

  /* ---------- Window control wiring + drag ---------- */
  windows.forEach((win) => {
    const name = win.getAttribute("data-window");
    const closeBtns = Array.from(win.querySelectorAll("[data-close-window]"));
    const minBtns = Array.from(win.querySelectorAll("[data-minimize-window]"));
    const maxBtns = Array.from(win.querySelectorAll("[data-maximize-window]"));
    const dragHandle = win.querySelector("[data-drag-handle]");

    closeBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        closeWindow(name);
      });
    });

    minBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        minimizeWindow(name);
      });
    });

    maxBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        toggleMaximize(name);
      });
    });

    if (dragHandle) {
      dragHandle.addEventListener("dblclick", (e) => {
        if (e.target.closest("button")) return;
        toggleMaximize(name);
      });
    }

    win.addEventListener("mousedown", () => bringToFront(win));

    if (!dragHandle) return;

    let dragging = false;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;

    function onPointerDown(e) {
      if (window.innerWidth <= 768) return;
      if (e.target.closest("button")) return;
      if (win.classList.contains("is-maximized")) return;

      const rect = win.getBoundingClientRect();
      win.style.left = `${rect.left}px`;
      win.style.top = `${rect.top}px`;
      win.style.transform = "";

      dragging = true;
      bringToFront(win);

      const point = e.touches ? e.touches[0] : e;
      startX = point.clientX;
      startY = point.clientY;
      startLeft = win.offsetLeft;
      startTop = win.offsetTop;

      document.body.style.userSelect = "none";
      win.style.transition = "none";

      e.preventDefault();
    }

    function onPointerMove(e) {
      if (!dragging) return;
      const point = e.touches ? e.touches[0] : e;
      const dx = point.clientX - startX;
      const dy = point.clientY - startY;

      // keep enough of the titlebar on-screen that the window can always be dragged back
      const minVisible = 80;
      const taskbarEl = document.querySelector(".xpTaskbar");
      const taskbarHeight = taskbarEl ? taskbarEl.offsetHeight : 0;

      const minLeft = -(win.offsetWidth - minVisible);
      const maxLeft = window.innerWidth - minVisible;
      const maxTop = window.innerHeight - taskbarHeight - 40;

      const nextLeft = Math.min(maxLeft, Math.max(minLeft, startLeft + dx));
      const nextTop = Math.min(maxTop, Math.max(0, startTop + dy));

      win.style.left = `${nextLeft}px`;
      win.style.top = `${nextTop}px`;
    }

    function onPointerUp() {
      if (!dragging) return;
      dragging = false;
      document.body.style.userSelect = "";
      win.style.transition = "";
      
      // Mark this window as user-positioned so smart positioning won't override it
      userPositionedWindows.add(name);
    }

    dragHandle.addEventListener("mousedown", onPointerDown);
    dragHandle.addEventListener("touchstart", onPointerDown, { passive: false });

    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("touchmove", onPointerMove, { passive: false });

    window.addEventListener("mouseup", onPointerUp);
    window.addEventListener("touchend", onPointerUp);
    window.addEventListener("touchcancel", onPointerUp);
  });

  /* ---------- Form-submit success ---------- */
  root.querySelectorAll("[data-xp-form-wrap]").forEach((wrap) => {
    const form = wrap.querySelector("form");
    if (!form) return;

    form.addEventListener("submit", () => {
      const formName = wrap.getAttribute("data-xp-form-wrap");
      if (!formName) return;
      sessionStorage.setItem("xpLastSubmittedForm", formName);
    });
  });

  const lastSubmittedForm = sessionStorage.getItem("xpLastSubmittedForm");
  // Several forms render a result marker after a post — find the one matching
  // the form that was actually submitted (tracked via sessionStorage).
  const submitResult =
    Array.from(root.querySelectorAll("[data-xp-submit-result]")).find(
      (el) => el.getAttribute("data-xp-submit-name") === lastSubmittedForm
    ) || null;

  if (lastSubmittedForm && submitResult) {
    const resultName = submitResult.getAttribute("data-xp-submit-name");
    const resultStatus = submitResult.getAttribute("data-xp-submit-status");

    if (resultName === lastSubmittedForm && resultStatus === "success") {
      const alertTitle = root.querySelector("[data-form-alert-title]");
      const alertText = root.querySelector("[data-form-alert-text]");

      const messages = {
        contact: {
          title: "Contact form sent",
          text: "Your contact message was sent successfully. We'll get back to you soon."
        },
        workwithus: {
          title: "Work With Us form sent",
          text: "Your collaboration request was sent successfully."
        },
        recruitment: {
          title: "Recruitment form sent",
          text: "Your recruitment application was sent successfully."
        },
        submission: {
          title: "Submission form sent",
          text: "Your submission was sent successfully."
        },
        services: {
          title: "Message sent",
          text: "Thanks, we got it. We'll reply to the e-mail address you gave us."
        }
      };

      const message = messages[lastSubmittedForm] || {
        title: "Form sent",
        text: "Your message was sent successfully."
      };

      if (alertTitle) alertTitle.textContent = message.title;
      if (alertText) alertText.textContent = message.text;

      setTimeout(() => openWindow("form-alert"), 60);
    }

    sessionStorage.removeItem("xpLastSubmittedForm");
  } else if (lastSubmittedForm) {
    setTimeout(() => openWindow(lastSubmittedForm), 40);
    sessionStorage.removeItem("xpLastSubmittedForm");
  }

  /* ========================================================
     MUSIC PLAYER - Direct Audio Playback
     ======================================================== */

  const musicPlayer = root.querySelector("[data-music-player]");
  
  if (musicPlayer) {
    const musicTab = root.querySelector("[data-music-tab]");
    const audio = musicPlayer.querySelector("[data-music-audio]");
    const artworkImg = musicPlayer.querySelector("[data-music-artwork-img]");
    const trackEl = musicPlayer.querySelector("[data-music-track]");
    const currentTimeEl = musicPlayer.querySelector("[data-music-current]");
    const durationEl = musicPlayer.querySelector("[data-music-duration]");
    const progressWrap = musicPlayer.querySelector("[data-music-progress-wrap]");
    const progressBar = musicPlayer.querySelector("[data-music-progress-bar]");
    const socialLinksEl = musicPlayer.querySelector("[data-music-social]");
    const playBtn = musicPlayer.querySelector("[data-music-play]");
    const prevBtn = musicPlayer.querySelector("[data-music-prev]");
    const nextBtn = musicPlayer.querySelector("[data-music-next]");
    const browseBtn = musicPlayer.querySelector("[data-music-browse]");
    const volumeSlider = musicPlayer.querySelector("[data-music-volume]");
    const volumeValue = musicPlayer.querySelector("[data-music-volume-value]");
    const menu = musicPlayer.querySelector("[data-music-menu]");
    const menuList = musicPlayer.querySelector("[data-music-menu-list]");
    const menuCloseBtn = musicPlayer.querySelector("[data-music-menu-close]");
    const closeBtn = musicPlayer.querySelector("[data-music-close]");
    const minimizeBtn = musicPlayer.querySelector("[data-music-minimize]");
    const dragHandle = musicPlayer.querySelector("[data-music-drag-handle]");

    const tracks = [
      {
        title: "Violet Eyes",
        artist: "IDLEGLANCE x Yates XV",
        audioUrl: "https://cdn.shopify.com/s/files/1/0555/7177/5836/files/violet_eyes.mp3?v=1777207375",
        artworkUrl: "https://cdn.shopify.com/s/files/1/0555/7177/5836/files/yates-final-song.jpg?v=1777207368",
        spotify: "https://open.spotify.com/track/15m25CwukEUpkSLbTJP4wh?si=78a7c66c556e4f4e", // Optional
        youtube: "https://www.youtube.com/watch?v=VXKyLK_YLMQ", // Optional
        soundcloud: "https://soundcloud.com/idleglance" // Optional
      }
    ];

    let currentIndex = 0;
    let isPlaying = false;

    function formatTime(seconds) {
      if (isNaN(seconds)) return "0:00";
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${String(secs).padStart(2, "0")}`;
    }

    function loadTrack(index) {
      if (index < 0 || index >= tracks.length) return;
      currentIndex = index;
      const track = tracks[currentIndex];

      audio.src = track.audioUrl;
      trackEl.textContent = track.title;

      artworkImg.classList.remove("is-loaded");
      artworkImg.src = track.artworkUrl;
      artworkImg.onload = () => artworkImg.classList.add("is-loaded");

      updateSocialIcons();
      updateMenuActive();
    }

    function updateSocialIcons() {
      if (!socialLinksEl) return;
      const track = tracks[currentIndex];
      
      let iconsHTML = '';
      if (track.spotify) {
        iconsHTML += `<a href="${track.spotify}" target="_blank" class="xpMusicPlayer__socialIcon" title="Spotify">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="#1DB954" d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
        </a>`;
      }
      if (track.youtube) {
        iconsHTML += `<a href="${track.youtube}" target="_blank" class="xpMusicPlayer__socialIcon" title="YouTube">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="#FF0000" d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
        </a>`;
      }
      if (track.soundcloud) {
        iconsHTML += `<a href="${track.soundcloud}" target="_blank" class="xpMusicPlayer__socialIcon" title="SoundCloud">
          <svg viewBox="0 0 17 17" xmlns="http://www.w3.org/2000/svg"><path fill="#FF5500" d="M0.882 10.132l-0.139 0.875c-0.007 0.035-0.027 0.062-0.062 0.062s-0.056-0.027-0.062-0.062l-0.119-0.875 0.118-0.889c0.007-0.035 0.028-0.062 0.062-0.062s0.055 0.027 0.062 0.062l0.14 0.889zM1.521 10.132l-0.18 1.409c-0.007 0.035-0.035 0.062-0.069 0.062-0.035 0-0.062-0.027-0.062-0.068l-0.16-1.403c0.16-1.438 0.16-1.438 0.16-1.438 0-0.034 0.027-0.062 0.062-0.062 0.034 0 0.062 0.028 0.069 0.062l0.18 1.438zM2.153 10.132l-0.173 1.646c0 0.041-0.035 0.076-0.077 0.076s-0.076-0.035-0.083-0.076l-0.146-1.646 0.146-1.701c0.007-0.049 0.042-0.083 0.083-0.083 0.042 0 0.077 0.034 0.077 0.083l0.173 1.701zM2.792 10.132l-0.16 1.694c-0.007 0.056-0.048 0.091-0.097 0.091-0.048 0-0.090-0.035-0.090-0.091l-0.146-1.694 0.146-1.75c0-0.056 0.042-0.090 0.090-0.090 0.049 0 0.090 0.034 0.097 0.090l0.16 1.75zM3.431 10.132l-0.146 1.709c-0.007 0.062-0.056 0.111-0.111 0.111s-0.104-0.049-0.104-0.111l-0.139-1.709 0.139-1.624c0-0.057 0.049-0.105 0.104-0.105 0.056 0 0.104 0.049 0.111 0.105l0.146 1.624zM4.083 10.132l-0.146 1.709c0 0.068-0.056 0.125-0.118 0.125-0.069 0-0.118-0.057-0.125-0.125l-0.125-1.709c0.125-2.64 0.125-2.64 0.125-2.64 0.008-0.068 0.056-0.124 0.125-0.124 0.062 0 0.118 0.056 0.118 0.124l0.146 2.64zM4.722 10.146l-0.132 1.695c0 0.076-0.063 0.131-0.132 0.131-0.076 0-0.131-0.055-0.139-0.131l-0.111-1.695c0.111-3.25 0.111-3.25 0.111-3.25 0.007-0.076 0.062-0.132 0.139-0.132 0.069 0 0.132 0.056 0.132 0.132l0.132 3.25zM5.396 10.132l-0.125 1.681c-0.007 0.083-0.069 0.146-0.153 0.146-0.076 0-0.139-0.062-0.146-0.146l-0.111-1.681 0.111-3.514c0-0.083 0.070-0.153 0.146-0.153 0.083 0 0.146 0.070 0.153 0.153l0.125 3.514zM6.056 10.132l-0.112-3.632c-0.006-0.090-0.076-0.167-0.166-0.167-0.083 0-0.16 0.077-0.16 0.167l-0.098 3.632 0.098 1.674c0.007 0.090 0.077 0.16 0.16 0.16 0.090 0 0.16-0.070 0.167-0.16l0.111-1.674zM6.722 10.132l-0.104 1.66c0 0.097-0.076 0.174-0.174 0.174-0.097 0-0.167-0.077-0.173-0.174l-0.098-1.66 0.098-3.542c0-0.098 0.076-0.174 0.173-0.174 0.098 0 0.174 0.076 0.174 0.174l0.104 3.542zM7.403 10.139l-0.098 1.64c0 0.104-0.083 0.188-0.187 0.188s-0.188-0.084-0.194-0.188l-0.083-1.64 0.083-3.416c0.007-0.111 0.090-0.195 0.194-0.195 0.104 0 0.18 0.084 0.187 0.195l0.098 3.416zM8.076 10.139l-0.083-4.070c0-0.069-0.035-0.132-0.090-0.166-0.035-0.021-0.070-0.035-0.111-0.035-0.042 0-0.076 0.014-0.111 0.035-0.056 0.035-0.091 0.097-0.091 0.166l-0.007 0.041-0.070 4.021c0 0 0 0.007 0.077 1.639 0 0 0 0 0 0.008 0 0.041 0.014 0.083 0.042 0.117 0.042 0.049 0.097 0.076 0.16 0.076 0.056 0 0.104-0.027 0.139-0.062 0.042-0.034 0.062-0.083 0.062-0.139l0.007-0.166 0.076-1.465zM8.681 11.743c0 0.118-0.097 0.215-0.215 0.215s-0.215-0.097-0.223-0.215l-0.041-0.791-0.042-0.813 0.083-4.416v-0.022c0.007-0.062 0.035-0.125 0.084-0.166 0.034-0.027 0.083-0.049 0.138-0.049 0.035 0 0.077 0.014 0.104 0.035 0.062 0.034 0.104 0.104 0.111 0.18l0.097 4.438-0.096 1.604zM14.535 11.966c-5.452 0-5.458 0-5.458 0-0.118-0.014-0.215-0.104-0.215-0.229v-6.245c0-0.117 0.042-0.173 0.195-0.229 0.382-0.152 0.812-0.235 1.257-0.235 1.812 0 3.299 1.388 3.458 3.159 0.236-0.097 0.493-0.153 0.764-0.153 1.083 0 1.965 0.883 1.965 1.973-0.001 1.084-0.883 1.959-1.966 1.959z"/></svg>
        </a>`;
      }
      
      socialLinksEl.innerHTML = iconsHTML;
    }

    function play() {
      audio.play();
      isPlaying = true;
      playBtn.textContent = "⏸";
    }

    function pause() {
      audio.pause();
      isPlaying = false;
      playBtn.textContent = "▶";
    }

    function togglePlayPause() {
      if (isPlaying) pause();
      else play();
    }

    function nextTrack() {
      loadTrack((currentIndex + 1) % tracks.length);
      if (isPlaying) play();
    }

    function prevTrack() {
      loadTrack((currentIndex - 1 + tracks.length) % tracks.length);
      if (isPlaying) play();
    }

    function updateProgress() {
      if (audio.duration) {
        const percent = (audio.currentTime / audio.duration) * 100;
        progressBar.style.width = `${percent}%`;
        currentTimeEl.textContent = formatTime(audio.currentTime);
        durationEl.textContent = formatTime(audio.duration);
      }
    }

    function seekTo(e) {
      const rect = progressWrap.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percent = x / rect.width;
      audio.currentTime = percent * audio.duration;
    }

    function buildMenu() {
      menuList.innerHTML = "";
      tracks.forEach((track, idx) => {
        const item = document.createElement("div");
        item.className = "xpMusicPlayer__menuItem";
        if (idx === currentIndex) item.classList.add("is-active");
        
        let socialLinks = '';
        if (track.spotify || track.youtube || track.soundcloud) {
          socialLinks = '<div class="xpMusicPlayer__menuItemLinks">';
          if (track.spotify) {
            socialLinks += `<a href="${track.spotify}" target="_blank" class="xpMusicPlayer__socialIcon" title="Spotify">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="#1DB954" d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
            </a>`;
          }
          if (track.youtube) {
            socialLinks += `<a href="${track.youtube}" target="_blank" class="xpMusicPlayer__socialIcon" title="YouTube">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="#FF0000" d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            </a>`;
          }
          if (track.soundcloud) {
            socialLinks += `<a href="${track.soundcloud}" target="_blank" class="xpMusicPlayer__socialIcon" title="SoundCloud">
              <svg viewBox="0 0 17 17" xmlns="http://www.w3.org/2000/svg"><path fill="#FF5500" d="M0.882 10.132l-0.139 0.875c-0.007 0.035-0.027 0.062-0.062 0.062s-0.056-0.027-0.062-0.062l-0.119-0.875 0.118-0.889c0.007-0.035 0.028-0.062 0.062-0.062s0.055 0.027 0.062 0.062l0.14 0.889zM1.521 10.132l-0.18 1.409c-0.007 0.035-0.035 0.062-0.069 0.062-0.035 0-0.062-0.027-0.062-0.068l-0.16-1.403c0.16-1.438 0.16-1.438 0.16-1.438 0-0.034 0.027-0.062 0.062-0.062 0.034 0 0.062 0.028 0.069 0.062l0.18 1.438zM2.153 10.132l-0.173 1.646c0 0.041-0.035 0.076-0.077 0.076s-0.076-0.035-0.083-0.076l-0.146-1.646 0.146-1.701c0.007-0.049 0.042-0.083 0.083-0.083 0.042 0 0.077 0.034 0.077 0.083l0.173 1.701zM2.792 10.132l-0.16 1.694c-0.007 0.056-0.048 0.091-0.097 0.091-0.048 0-0.090-0.035-0.090-0.091l-0.146-1.694 0.146-1.75c0-0.056 0.042-0.090 0.090-0.090 0.049 0 0.090 0.034 0.097 0.090l0.16 1.75zM3.431 10.132l-0.146 1.709c-0.007 0.062-0.056 0.111-0.111 0.111s-0.104-0.049-0.104-0.111l-0.139-1.709 0.139-1.624c0-0.057 0.049-0.105 0.104-0.105 0.056 0 0.104 0.049 0.111 0.105l0.146 1.624zM4.083 10.132l-0.146 1.709c0 0.068-0.056 0.125-0.118 0.125-0.069 0-0.118-0.057-0.125-0.125l-0.125-1.709c0.125-2.64 0.125-2.64 0.125-2.64 0.008-0.068 0.056-0.124 0.125-0.124 0.062 0 0.118 0.056 0.118 0.124l0.146 2.64zM4.722 10.146l-0.132 1.695c0 0.076-0.063 0.131-0.132 0.131-0.076 0-0.131-0.055-0.139-0.131l-0.111-1.695c0.111-3.25 0.111-3.25 0.111-3.25 0.007-0.076 0.062-0.132 0.139-0.132 0.069 0 0.132 0.056 0.132 0.132l0.132 3.25zM5.396 10.132l-0.125 1.681c-0.007 0.083-0.069 0.146-0.153 0.146-0.076 0-0.139-0.062-0.146-0.146l-0.111-1.681 0.111-3.514c0-0.083 0.070-0.153 0.146-0.153 0.083 0 0.146 0.070 0.153 0.153l0.125 3.514zM6.056 10.132l-0.112-3.632c-0.006-0.090-0.076-0.167-0.166-0.167-0.083 0-0.16 0.077-0.16 0.167l-0.098 3.632 0.098 1.674c0.007 0.090 0.077 0.16 0.16 0.16 0.090 0 0.16-0.070 0.167-0.16l0.111-1.674zM6.722 10.132l-0.104 1.66c0 0.097-0.076 0.174-0.174 0.174-0.097 0-0.167-0.077-0.173-0.174l-0.098-1.66 0.098-3.542c0-0.098 0.076-0.174 0.173-0.174 0.098 0 0.174 0.076 0.174 0.174l0.104 3.542zM7.403 10.139l-0.098 1.64c0 0.104-0.083 0.188-0.187 0.188s-0.188-0.084-0.194-0.188l-0.083-1.64 0.083-3.416c0.007-0.111 0.090-0.195 0.194-0.195 0.104 0 0.18 0.084 0.187 0.195l0.098 3.416zM8.076 10.139l-0.083-4.070c0-0.069-0.035-0.132-0.090-0.166-0.035-0.021-0.070-0.035-0.111-0.035-0.042 0-0.076 0.014-0.111 0.035-0.056 0.035-0.091 0.097-0.091 0.166l-0.007 0.041-0.070 4.021c0 0 0 0.007 0.077 1.639 0 0 0 0 0 0.008 0 0.041 0.014 0.083 0.042 0.117 0.042 0.049 0.097 0.076 0.16 0.076 0.056 0 0.104-0.027 0.139-0.062 0.042-0.034 0.062-0.083 0.062-0.139l0.007-0.166 0.076-1.465zM8.681 11.743c0 0.118-0.097 0.215-0.215 0.215s-0.215-0.097-0.223-0.215l-0.041-0.791-0.042-0.813 0.083-4.416v-0.022c0.007-0.062 0.035-0.125 0.084-0.166 0.034-0.027 0.083-0.049 0.138-0.049 0.035 0 0.077 0.014 0.104 0.035 0.062 0.034 0.104 0.104 0.111 0.18l0.097 4.438-0.096 1.604zM14.535 11.966c-5.452 0-5.458 0-5.458 0-0.118-0.014-0.215-0.104-0.215-0.229v-6.245c0-0.117 0.042-0.173 0.195-0.229 0.382-0.152 0.812-0.235 1.257-0.235 1.812 0 3.299 1.388 3.458 3.159 0.236-0.097 0.493-0.153 0.764-0.153 1.083 0 1.965 0.883 1.965 1.973-0.001 1.084-0.883 1.959-1.966 1.959z"/></svg>
            </a>`;
          }
          socialLinks += '</div>';
        }
        
        item.innerHTML = `
          <div class="xpMusicPlayer__menuItemArt">
            <img src="${track.artworkUrl}" alt="${track.title}">
          </div>
          <div class="xpMusicPlayer__menuItemInfo">
            <div class="xpMusicPlayer__menuItemTrack">${track.title}</div>
            <div class="xpMusicPlayer__menuItemArtist">${track.artist}</div>
            ${socialLinks}
          </div>
        `;

        item.addEventListener("click", (e) => {
          // Don't change track if clicking on a social link
          if (e.target.closest('.xpMusicPlayer__socialIcon')) return;
          loadTrack(idx);
          if (isPlaying) play();
          closeMenu();
        });

        menuList.appendChild(item);
      });
    }

    function openMenu() {
      menu.classList.add("is-visible");
      buildMenu();
    }

    function closeMenu() {
      menu.classList.remove("is-visible");
    }

    function updateMenuActive() {
      const items = menuList.querySelectorAll(".xpMusicPlayer__menuItem");
      items.forEach((item, idx) => {
        item.classList.toggle("is-active", idx === currentIndex);
      });
    }

    if (playBtn) playBtn.addEventListener("click", togglePlayPause);
    if (nextBtn) nextBtn.addEventListener("click", nextTrack);
    if (prevBtn) prevBtn.addEventListener("click", prevTrack);
    if (browseBtn) {
      browseBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (menu.classList.contains("is-visible")) closeMenu();
        else openMenu();
      });
    }

    if (menuCloseBtn) {
      menuCloseBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        closeMenu();
      });
    }

    if (progressWrap) progressWrap.addEventListener("click", seekTo);

    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        musicPlayer.classList.remove("is-visible");
        musicPlayer.classList.add("is-minimized");
        if (musicTab) musicTab.classList.add("is-visible");
        pause();
      });
    }

    if (musicTab) {
      musicTab.addEventListener("click", () => {
        musicPlayer.classList.add("is-visible");
        musicPlayer.classList.remove("is-minimized");
        musicTab.classList.remove("is-visible");
      });
    }

    if (minimizeBtn) {
      minimizeBtn.addEventListener("click", () => {
        musicPlayer.classList.remove("is-visible");
        musicPlayer.classList.add("is-minimized");
        if (musicTab) musicTab.classList.add("is-visible");
        pause();
      });
    }

    if (volumeSlider && audio) {
      volumeSlider.value = 5;
      audio.volume = 0.05;
      if (volumeValue) volumeValue.textContent = "5%";
      
      volumeSlider.addEventListener("input", (e) => {
        const vol = e.target.value;
        audio.volume = vol / 100;
        if (volumeValue) volumeValue.textContent = `${vol}%`;
      });
    }

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", nextTrack);
    audio.addEventListener("loadedmetadata", () => {
      durationEl.textContent = formatTime(audio.duration);
    });

    document.addEventListener("click", (e) => {
      if (menu.classList.contains("is-visible") &&
          !menu.contains(e.target) &&
          !browseBtn.contains(e.target)) {
        closeMenu();
      }
    });

    if (dragHandle) {
      let dragging = false;
      let startX, startY, startRight, startBottom;

      dragHandle.addEventListener("mousedown", (e) => {
        if (e.target.closest("button")) return;
        dragging = true;
        startX = e.clientX;
        startY = e.clientY;
        
        const rect = musicPlayer.getBoundingClientRect();
        startRight = window.innerWidth - rect.right;
        startBottom = window.innerHeight - rect.bottom;
        
        musicPlayer.style.transition = "none";
        e.preventDefault();
      });

      window.addEventListener("mousemove", (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        const newRight = Math.max(10, startRight - dx);
        const newBottom = Math.max(10, startBottom - dy);

        musicPlayer.style.right = `${newRight}px`;
        musicPlayer.style.bottom = `${newBottom}px`;
      });

      window.addEventListener("mouseup", () => {
        if (dragging) {
          dragging = false;
          musicPlayer.style.transition = "";
        }
      });
    }

    loadTrack(0);

    let autoplayEnabled = false;
    const enableAutoplay = () => {
      if (!autoplayEnabled) {
        autoplayEnabled = true;
        document.removeEventListener("click", enableAutoplay);
        document.removeEventListener("keydown", enableAutoplay);
      }
    };
    document.addEventListener("click", enableAutoplay, { once: true });
    document.addEventListener("keydown", enableAutoplay, { once: true });

    setTimeout(() => {
      musicPlayer.classList.add("is-visible");

      const attemptAutoplay = () => {
        audio.play().then(() => {
          isPlaying = true;
          playBtn.textContent = "⏸";
        }).catch(() => {
          // blocked — retry once the user interacts with the page
          const retryPlay = () => {
            if (autoplayEnabled) {
              audio.play().then(() => {
                isPlaying = true;
                playBtn.textContent = "⏸";
              }).catch(() => {});
              document.removeEventListener("click", retryPlay);
            }
          };
          document.addEventListener("click", retryPlay, { once: true });
        });
      };
      
      attemptAutoplay();
    }, 3400); // waits for the boot screen to finish before sliding in
  }

  /* ---------- Merch folders ---------- */
  document.querySelectorAll(".xpMerchFolder").forEach((folder) => {
    folder.addEventListener("click", () => {
      const dropId = folder.getAttribute("data-drop");
      if (!dropId) return;

      const peeks = Array.from(folder.querySelectorAll(".xpMerchFolder__peek"));
      const dropWin = document.querySelector(`[data-window="${dropId}"]`);
      const grid = dropWin ? dropWin.querySelector("[data-drop-grid]") : null;
      const slots = grid ? Array.from(grid.querySelectorAll(".xpDropLook__slot")) : [];

      openWindow(dropId, folder);
      slots.forEach(s => s.classList.remove("is-visible"));

      // clone each peek item and fly the clone to its slot, so the real
      // slot content only appears once the flight animation lands
      peeks.forEach((peek, i) => {
        if (!slots[i]) return;
        const slot = slots[i];

        const peekRect = peek.getBoundingClientRect();
        const clone = peek.cloneNode(true);
        clone.style.cssText = `
          position: fixed;
          left: ${peekRect.left}px;
          top: ${peekRect.top}px;
          width: ${peekRect.width}px;
          height: ${peekRect.height}px;
          z-index: 99999;
          pointer-events: none;
          transition: left 0.45s cubic-bezier(0.4, 0, 0.2, 1),
                      top 0.45s cubic-bezier(0.4, 0, 0.2, 1),
                      width 0.45s ease,
                      height 0.45s ease,
                      opacity 0.15s ease 0.38s;
          border-radius: inherit;
        `;
        document.body.appendChild(clone);

        // staggered per item so they don't all fly at once
        setTimeout(() => {
          const updatedSlotRect = slot.getBoundingClientRect();
          clone.style.left = `${updatedSlotRect.left + 10}px`;
          clone.style.top = `${updatedSlotRect.top + 10}px`;
          clone.style.width = `${updatedSlotRect.width - 20}px`;
          clone.style.height = `${updatedSlotRect.height - 20}px`;
          clone.style.opacity = "0";

          setTimeout(() => {
            slot.classList.add("is-visible");
            clone.remove();
          }, 460);
        }, 80 + i * 120);
      });

      // If more slots than peek items, animate remaining in sequence
      slots.forEach((slot, i) => {
        if (i >= peeks.length) {
          setTimeout(() => slot.classList.add("is-visible"), 200 + i * 140);
        }
      });
    });
  });

  /* ---------- Background chaos (blurred construction activity) ---------- */
  const chaosLayer = root.querySelector("[data-xp-chaos]");
  const prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (chaosLayer && !prefersReducedMotion) {
    const EMOJIS = ["🚧", "🛠", "🔨", "💾", "📁", "💿", "🎵", "🖱", "⚠", "✨", "🧱", "📀", "🔧", "💽", "🖥"];
    const TEXTS = [
      "compiling idleglance.exe ...",
      "copying merch.dll ...",
      "rebuilding lookbook_2026 ...",
      "loading drops ... 47%",
      "C:\\IDLEGLANCE\\> _",
      "installing update 3 of 9741",
      "defragmenting vibes ...",
      "ERROR: too much sauce",
      "01101001 01100100 01101100",
      "burning mixtape.iso ...",
      "> press any key",
      "reticulating splines ..."
    ];
    const TITLES = [
      "merch.dll",
      "about_us.txt",
      "drop_2026.psd",
      "idleglance.exe",
      "lookbook.mov",
      "recruit.db",
      "untitled - Paint",
      "C:\\WINDOWS\\system32"
    ];

    const isSmallScreen = window.innerWidth <= 768;
    const MAX_ELEMENTS = isSmallScreen ? 9 : 18;

    const rand = (min, max) => min + Math.random() * (max - min);
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

    function spawnWindow() {
      const el = document.createElement("div");
      el.className = "xpChaosWin";
      el.style.width = `${rand(160, isSmallScreen ? 260 : 420)}px`;
      el.style.height = `${rand(100, 240)}px`;
      el.style.left = `${rand(-5, 78)}%`;
      el.style.top = `${rand(-5, 72)}%`;

      const body =
        Math.random() < 0.45
          ? `<div class="xpChaosWin__progress"><div class="xpChaosWin__progressFill"></div></div>`
          : `<div class="xpChaosWin__line" style="width:${rand(40, 95)}%"></div>
             <div class="xpChaosWin__line" style="width:${rand(30, 90)}%"></div>
             <div class="xpChaosWin__line" style="width:${rand(20, 70)}%"></div>`;

      el.innerHTML = `
        <div class="xpChaosWin__bar"><span>${pick(TITLES)}</span><span class="xpChaosWin__x"></span></div>
        <div class="xpChaosWin__body">${body}</div>
      `;
      chaosLayer.appendChild(el);

      setTimeout(() => {
        el.classList.add("is-dying");
        setTimeout(() => el.remove(), 350);
      }, rand(2200, 6000));
    }

    function spawnEmoji() {
      const el = document.createElement("div");
      el.className = "xpChaosEmoji";
      el.textContent = pick(EMOJIS);
      el.style.left = `${rand(2, 90)}%`;
      el.style.top = `${rand(8, 85)}%`;
      el.style.fontSize = `${rand(34, 92)}px`;
      chaosLayer.appendChild(el);
      el.addEventListener("animationend", () => el.remove());
    }

    function spawnText() {
      const el = document.createElement("div");
      el.className = "xpChaosText";
      el.textContent = pick(TEXTS);
      el.style.left = `${rand(2, 55)}%`;
      el.style.top = `${rand(5, 90)}%`;
      el.style.fontSize = `${rand(14, 26)}px`;
      el.style.color = pick(["#c3ff8a", "#8ad4ff", "#ffd94a", "#ff9a8a", "#ffffff"]);
      chaosLayer.appendChild(el);
      el.addEventListener("animationend", () => el.remove());
    }

    function spawnBsod() {
      const el = document.createElement("div");
      el.className = "xpChaosBsod";
      el.innerHTML =
        "A problem has been detected and IDLEGLANCE has been shut down to prevent damage.<br><br>" +
        "VIBE_OVERFLOW_EXCEPTION<br><br>" +
        "* Rebuilding in progress...<br>" +
        "* Do not turn off your computer.";
      chaosLayer.appendChild(el);
      el.addEventListener("animationend", () => el.remove());
    }

    function chaosTick() {
      if (chaosLayer.childElementCount < MAX_ELEMENTS) {
        const r = Math.random();
        if (r < 0.4) spawnWindow();
        else if (r < 0.7) spawnEmoji();
        else spawnText();
      }
      setTimeout(chaosTick, rand(300, 900));
    }

    function bsodLoop() {
      setTimeout(() => {
        spawnBsod();
        bsodLoop();
      }, rand(24000, 45000));
    }

    // seed a batch right away so the desktop is alive as soon as boot fades
    for (let i = 0; i < (isSmallScreen ? 3 : 6); i++) {
      setTimeout(spawnWindow, 300 + i * 220);
      setTimeout(spawnEmoji, 480 + i * 260);
    }

    setTimeout(chaosTick, 1200);
    bsodLoop();
  }

  /* ---------- Recruitment age gate (16+) ---------- */
  const recruitmentWrap = root.querySelector('[data-xp-form-wrap="recruitment"]');
  if (recruitmentWrap) {
    const recForm = recruitmentWrap.querySelector("form");
    const ageInput = recForm
      ? recForm.querySelector('input[name="contact[age]"]')
      : null;

    if (recForm && ageInput) {
      recForm.addEventListener("submit", (e) => {
        const age = parseInt(ageInput.value, 10);
        if (!isNaN(age) && age < 16) {
          e.preventDefault();
          // undo the "form submitted" marker set by the generic handler
          try { sessionStorage.removeItem("xpLastSubmittedForm"); } catch (err) {}
          openWindow("age-alert");
        }
      });
    }
  }

  /* ---------- Legal window jump links ---------- */
  root.querySelectorAll("[data-legal-jump]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const sectionId = btn.getAttribute("data-legal-jump");
      openWindow("legal", btn);

      // wait for the open animation, then scroll the body to the section
      setTimeout(() => {
        const legalWin = getWindow("legal");
        if (!legalWin) return;
        const body = legalWin.querySelector(".xpWindow__body");
        const target = legalWin.querySelector(`[data-legal-section="${sectionId}"]`);
        if (!body || !target) return;
        body.scrollTop =
          target.getBoundingClientRect().top -
          body.getBoundingClientRect().top +
          body.scrollTop -
          8;
      }, 320);
    });
  });

  /* ---------- Fake build progress (slow 1% random walk from 53%) ---------- */
  const progressFill = root.querySelector("[data-xp-progress]");
  if (progressFill) {
    const MIN = 35;
    const MAX = 88;
    let value = 53;
    let target = 70;

    function pickTarget() {
      // wander: sometimes climb, sometimes fall back, by a random amount
      const direction = Math.random() < 0.55 ? 1 : -1;
      const distance = 6 + Math.floor(Math.random() * 20);
      target = Math.max(MIN, Math.min(MAX, value + direction * distance));
      if (target === value) target = value + (value < 60 ? 8 : -8);
    }

    function stepProgress() {
      if (value === target) pickTarget();
      value += target > value ? 1 : -1;
      progressFill.style.width = `${value}%`;
      // slow, uneven ticks — 1% at a time
      setTimeout(stepProgress, 600 + Math.random() * 1400);
    }

    setTimeout(stepProgress, 2500);
  }

  /* ---------- Merchandise balloon ---------- */
  const balloon = root.querySelector("#xpMerchBalloon");
  const balloonBtn = root.querySelector("[data-xp-balloon-toggle]");
  if (balloon && balloonBtn) {
    function openBalloon() {
      balloon.classList.remove("is-closing");
      balloon.hidden = false;
      balloon.classList.add("is-open");
      balloonBtn.setAttribute("aria-expanded", "true");
    }

    function closeBalloon() {
      if (balloon.hidden) return;
      balloonBtn.setAttribute("aria-expanded", "false");
      balloon.classList.remove("is-open");
      balloon.classList.add("is-closing");
      setTimeout(() => {
        balloon.hidden = true;
        balloon.classList.remove("is-closing");
      }, 140);
    }

    balloonBtn.addEventListener("click", (event) => {
      event.stopPropagation();
      if (balloon.hidden) openBalloon();
      else closeBalloon();
    });

    const balloonClose = balloon.querySelector("[data-xp-balloon-close]");
    if (balloonClose) balloonClose.addEventListener("click", closeBalloon);

    document.addEventListener("click", (event) => {
      if (!balloon.contains(event.target)) closeBalloon();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeBalloon();
    });
  }

  /* ---------- Init ---------- */
  updateClock();
  setInterval(updateClock, 1000 * 20);
})();